import { action, computed, makeObservable, observable, runInAction } from "mobx"

const closeSidebarKey = "_code_close_sidebar"
const closeSidebarSmallScreenKey = "_code_close_sidebar_small"

class CodeSidebarManager {
    @observable private manuallyOpened = true
    @observable private taskPaneSidebarOpen = false
    @observable private localTaskPaneDocked = false
    @observable private globalBrowserDocked = false
    @observable isSmallScreen = false
    private mediaQueryListener: MediaQueryList | null = null

    constructor() {
        makeObservable(this)
        this.setupMediaQueryListener()
        this.initializeSidebarState()
    }

    private setupMediaQueryListener() {
        if (typeof window !== "undefined") {
            this.mediaQueryListener = window.matchMedia("(max-width: 900px)")
            this.isSmallScreen = this.mediaQueryListener.matches
            this.mediaQueryListener.addEventListener("change", this.handleMediaQueryChange)
        }
    }

    private initializeSidebarState() {
        if (typeof window === "undefined" || typeof localStorage === "undefined") {
            this.manuallyOpened = false
            return
        }

        if (this.isSmallScreen) {
            const smallScreenPreference = localStorage.getItem(closeSidebarSmallScreenKey)
            if (smallScreenPreference === null) {
                this.manuallyOpened = false
            } else {
                this.manuallyOpened = !(smallScreenPreference === "true")
            }
        } else {
            this.manuallyOpened = !(localStorage.getItem(closeSidebarKey) === "true")
        }
    }

    @computed get showSidebar() {
        return this.taskPaneDocked ? this.taskPaneSidebarOpen : this.manuallyOpened
    }

    @computed get taskPaneDocked() {
        return this.localTaskPaneDocked || this.globalBrowserDocked
    }

    @computed get isDrawerMode() {
        return this.isSmallScreen && this.manuallyOpened
    }

    @action.bound toggleSidebar() {
        if (this.taskPaneDocked) {
            this.taskPaneSidebarOpen = !this.taskPaneSidebarOpen
            return
        }
        this.manuallyOpened = !this.manuallyOpened

        if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
            if (this.isSmallScreen) {
                if (this.manuallyOpened === false) {
                    localStorage.setItem(closeSidebarSmallScreenKey, "true")
                } else {
                    localStorage.removeItem(closeSidebarSmallScreenKey)
                }
            } else {
                if (this.manuallyOpened === false) {
                    localStorage.setItem(closeSidebarKey, "true")
                } else {
                    localStorage.removeItem(closeSidebarKey)
                }
            }
        }
    }

    @action.bound closeSidebarForNavigation() {
        if (!this.isSmallScreen || !this.manuallyOpened) return
        this.manuallyOpened = false
        if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
            localStorage.setItem(closeSidebarSmallScreenKey, "true")
        }
    }

    @action.bound setTaskPaneDocked(docked: boolean) {
        const wasDocked = this.taskPaneDocked
        this.localTaskPaneDocked = docked
        this.finishDockedStateChange(wasDocked)
    }

    @action.bound setGlobalBrowserDocked(docked: boolean) {
        const wasDocked = this.taskPaneDocked
        this.globalBrowserDocked = docked
        this.finishDockedStateChange(wasDocked)
    }

    dispose() {
        if (this.mediaQueryListener) {
            this.mediaQueryListener.removeEventListener("change", this.handleMediaQueryChange)
        }
    }

    private handleMediaQueryChange = (e: MediaQueryListEvent) => {
        runInAction(() => {
            const wasSmallScreen = this.isSmallScreen
            this.isSmallScreen = e.matches

            if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
                if (!wasSmallScreen && this.isSmallScreen) {
                    this.manuallyOpened = false
                    localStorage.removeItem(closeSidebarSmallScreenKey)
                } else if (wasSmallScreen && !this.isSmallScreen) {
                    this.manuallyOpened = !(localStorage.getItem(closeSidebarKey) === "true")
                }
            }
        })
    }

    private finishDockedStateChange(wasDocked: boolean) {
        if (!wasDocked && this.taskPaneDocked) this.taskPaneSidebarOpen = false
        if (!this.taskPaneDocked) this.taskPaneSidebarOpen = false
    }
}

export const codeSidebarManager = new CodeSidebarManager()
